import { useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
  Svg,
  Path,
} from '@react-pdf/renderer';
import ibmLogo from '../../../public/images/ibmlogo.png';
import { Step5Props } from '../../../lib/interfaces';
import { defaultValues } from 'src/apps/solution-advisor/constants/default';

export const PDFGenerate = ({
  pdfImage3String,
  pdfPortfolioString,
  productObj,
  solutionData,
  localeCode,
  useTranslation,
}: Step5Props) => {
  Font.registerHyphenationCallback((word) => [word]);
  const page1Items = defaultValues('pdfPage1', useTranslation) || [];

  const styles = StyleSheet.create({
    page: {
      paddingTop: 35,
      paddingBottom: 65,
      paddingHorizontal: 35,
      fontSize: 10,
      lineHeight: 1.3,
    },
    title: { fontSize: 14, paddingBottom: 20, fontFamily: 'Helvetica-Bold' },
    blueTitle: {
      fontSize: 12,
      color: 'blue',
      paddingBottom: 10,
      fontFamily: 'Helvetica-Bold',
    },
    titleFont12: { fontSize: 12 },
    subTitle: { paddingBottom: 25 },
    price: {
      paddingBottom: 25,
      paddingLeft: 40,
      fontSize: 14,
      fontFamily: 'Helvetica-Bold',
    },
    centerView: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      paddingBottom: 20,
    },
    image: {
      height: 100,
      width: 150,
    },
    subImage: {
      width: 30,
      height: 15,
      marginLeft: 50,
      position: 'absolute',
      fontSize: 12,
      bottom: 60,
      left: 0,
      right: 0,
      textAlign: 'left',
      color: 'grey',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 12,
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
    radarImage: {
      width: 350,
      height: 200,
      paddingLeft: 40,
      paddingBottom: 20,
    },
    portfolioImage: {
      width: '100%',
      height: 200,
      paddingBottom: 20,
    },
    btnLink: {
      backgroundColor: '#0d63fe',
      color: '#FFFFFF',
      padding: 10,
      borderRadius: 5,
      textAlign: 'center',
      width: '100%',
      marginBottom: 25,
      textDecoration: 'none',
    },
    table: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      fontSize: 9,
      borderColor: '#000',
      borderStyle: 'solid',
    },
    table2: { width: '70%', borderWidth: 1 },
    tableRow: {
      width: '100%',
      marginTop: 8,
      display: 'flex',
      flexDirection: 'row',
    },
    tableRowSingle: { flexDirection: 'row', alignItems: 'flex-start' },
    tableCell: {
      padding: 4,
      width: '35%',
      borderColor: '#000',
      borderStyle: 'solid',
    },
    tableCellBold: {
      paddingRight: 14,
      fontFamily: 'Helvetica-Bold',
    },
    multiCell: {
      padding: 4,
    },
    innerText: {
      marginTop: 8,
    },
    bold: {
      fontFamily: 'Helvetica-Bold',
    },
    m_r_5: {
      marginRight: 16,
    },
    p_t_15: {
      paddingTop: 15,
    },
    font_7: { fontSize: 7 },
    m_t_0: {
      marginTop: 0,
    },
    p_d_20: {
      paddingBottom: 20,
    },
  });

  const [currentDate, setCurrentDate] = useState<string>('');

  function formatDate() {
    const date = new Date();

    const formatter = new Intl.DateTimeFormat(localeCode, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedDate = formatter.format(date);
    const getDaySuffix = (day: number) => {
      if (day >= 11 && day <= 13) {
        return useTranslation('pdf_date_th', true) as string;
      }
      switch (day % 10) {
        case 1:
          return useTranslation('pdf_date_st', true) as string;
        case 2:
          return useTranslation('pdf_date_nd', true) as string;
        case 3:
          return useTranslation('pdf_date_rd', true) as string;
        default:
          return useTranslation('pdf_date_th', true) as string;
      }
    };
    const day = date.getDate();
    const daySuffix = getDaySuffix(day);
    const result = formattedDate.replace(/(\d+)/, day + daySuffix);
    return result;
  }

  useEffect(() => {
    setCurrentDate(formatDate());
  }, []);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          {useTranslation('pdf_page1_title', true)}
        </Text>
        <Text style={styles.p_d_20}>{currentDate}</Text>
        <View style={styles.centerView}>
          <Svg viewBox="0 0 32 32" style={styles.image}>
            <Path
              d="M30,30.36H2c-0.75,0-1.36-0.61-1.36-1.36V7c0-0.75,0.61-1.36,1.36-1.36h8.5c0.426,0,0.833,0.203,1.088,0.544l1.416,2.223
            C13.108,8.544,13.299,8.64,13.5,8.64H30c0.75,0,1.36,0.61,1.36,1.36v19C31.36,29.75,30.75,30.36,30,30.36z M24.36,29.64H30
            c0.353,0,0.64-0.287,0.64-0.64V10c0-0.353-0.287-0.64-0.64-0.64H13.5c-0.426,0-0.833-0.203-1.088-0.544l-1.416-2.223
            C10.892,6.456,10.701,6.36,10.5,6.36H2C1.647,6.36,1.36,6.647,1.36,7v22c0,0.353,0.287,0.64,0.64,0.64h5.64V13.312
            c-0.576-0.158-1-0.686-1-1.312c0-0.75,0.61-1.36,1.36-1.36S9.36,11.25,9.36,12c0,0.625-0.424,1.153-1,1.312V29.64h7.28V17.312
            c-0.576-0.158-1-0.686-1-1.312c0-0.75,0.61-1.36,1.36-1.36c0.75,0,1.36,0.61,1.36,1.36c0,0.625-0.425,1.153-1,1.312V29.64h7.28
            v-8.328c-0.575-0.158-1-0.687-1-1.312c0-0.75,0.61-1.36,1.36-1.36s1.36,0.61,1.36,1.36c0,0.625-0.425,1.153-1,1.312V29.64z
            M24,19.36c-0.353,0-0.64,0.287-0.64,0.64s0.287,0.64,0.64,0.64s0.64-0.287,0.64-0.64S24.353,19.36,24,19.36z M16,15.36
            c-0.353,0-0.64,0.287-0.64,0.64s0.287,0.64,0.64,0.64s0.64-0.287,0.64-0.64S16.353,15.36,16,15.36z M8,11.36
            c-0.353,0-0.64,0.287-0.64,0.64S7.647,12.64,8,12.64c0.353,0,0.64-0.287,0.64-0.64S8.353,11.36,8,11.36z"
              fill="#000000"
            />
          </Svg>
        </View>
        <Text style={styles.title}>
          {useTranslation('pdf_page2_title', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page2_para1', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page2_para2', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page2_para3', true)}
        </Text>
        <View style={styles.subTitle}>
          {page1Items.map((item, index) => (
            <View key={index} style={styles.tableRowSingle}>
              <Text style={styles.m_r_5}>{index + 1}.</Text>
              <Text>{item}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page2_para4', true)}
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, { width: '35%' }]}>
              <Link
                src={`https://www.ibm.com/flashsystem/pricing/contact`}
                style={styles.btnLink}
              >
                {useTranslation('pdf_page2_btn1', true)}
              </Link>
            </View>
            <View style={[styles.tableCell, { width: '35%' }]}>
              <Link
                src={`https://www.ibm.com/flashsystem/pricing`}
                style={styles.btnLink}
              >
                {useTranslation('pdf_page2_btn2', true)}
              </Link>
            </View>
          </View>
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          {useTranslation('pdf_page3_title', true)}
        </Text>
        <Image style={styles.radarImage} src={pdfImage3String} />
        <Text style={styles.blueTitle}>{productObj?.offname}</Text>
        <View style={[styles.table, styles.table2]}>
          {productObj?.product_serviceconfig &&
            !!productObj.product_serviceconfig.length &&
            productObj.product_serviceconfig.map(
              (service: string, i: number) => (
                <View key={i} style={[styles.tableRow, styles.m_t_0]}>
                  <View
                    style={[
                      styles.tableCell,
                      { borderTopWidth: i === 0 ? 0 : 1, flex: 1 },
                    ]}
                  >
                    <Text>{service}</Text>
                  </View>
                </View>
              ),
            )}
        </View>
        <Text style={[styles.subTitle, styles.p_t_15]}>
          {useTranslation(
            productObj?.offprice ? 'pdf_page3_price' : 'pdf_page3_price_no',
            true,
          )}
        </Text>
        {productObj?.offprice && (
          <Text style={styles.price}>{productObj?.offprice}</Text>
        )}

        <Text style={[styles.subTitle, styles.font_7]}>
          {useTranslation(
            productObj?.offprice ? 'pdf_page3_para_price' : 'pdf_page3_para',
            true,
          )}
        </Text>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          {useTranslation('pdf_page4_title', true)}
        </Text>
        <View style={styles.table}>
          {solutionData?.map((item, key) => {
            const borderBottomWidth = key === solutionData.length - 1 ? 0 : 1;
            return (
              <View style={styles.tableRow} key={key}>
                <View
                  style={[
                    styles.tableCell,
                    styles.tableCellBold,
                    {
                      borderBottomWidth,
                    },
                  ]}
                >
                  <Text>{item.label}</Text>
                </View>
                <View
                  style={[
                    item.isMultiple ? styles.multiCell : styles.tableCell,
                    {
                      width: '65%',
                      borderBottomWidth,
                    },
                  ]}
                >
                  {item.isMultiple ? (
                    item.value.map((inner: any, i: number) => {
                      const marginTop = i === 0 ? 0 : 8;
                      return typeof inner === 'object' ? (
                        <Link
                          style={[{ marginTop }]}
                          src={inner.url || ''}
                          key={i}
                        >
                          {inner.text}
                        </Link>
                      ) : (
                        <Text style={[{ marginTop }]} key={i}>
                          {inner}
                        </Text>
                      );
                    })
                  ) : (
                    <Text>{item.value}</Text>
                  )}
                </View>
              </View>
            );
          })}
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          {useTranslation('pdf_page5_title', true)}
        </Text>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page5_h1', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page5_para1', true)}
        </Text>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_1', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_1_p', true)}</Text>
        </View>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_2', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_2_p', true)}</Text>
        </View>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_3', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_3_p', true)}</Text>
        </View>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_4', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_4_p', true)}</Text>
        </View>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_5', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_5_p', true)}</Text>
        </View>
        <View style={styles.subTitle}>
          <Text style={styles.bold}>
            {useTranslation('pdf_page5_subTitle_6', true)}
          </Text>
          <Text>{useTranslation('pdf_page5_subTitle_6_p', true)}</Text>
        </View>
        <Image style={styles.portfolioImage} src={pdfPortfolioString} />
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page6_subTitle_1', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page6_subTitle_1_p', true)}
        </Text>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page6_subTitle_2', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page6_subTitle_2_p1', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page6_subTitle_2_p2', true)}
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, { width: '35%' }]}>
              <Link
                src={`https://www.ibm.com/flashsystem/pricing/`}
                style={styles.btnLink}
              >
                {useTranslation('pdf_page6_subTitle_2_p2_btn', true)}
              </Link>
            </View>
          </View>
        </View>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page6_subTitle_2_p3', true)}
        </Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, { width: '35%' }]}>
              <Link
                src={`https://www.ibm.com/flashsystem/cyber-resilience`}
                style={styles.btnLink}
              >
                {useTranslation('pdf_page6_subTitle_2_p3_btn', true)}
              </Link>
            </View>
          </View>
        </View>
      </Page>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          {useTranslation('pdf_page7_title', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page7_para1', true)}
        </Text>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page7_subTitle_1', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page7_subTitle_1_p', true)}
        </Text>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page7_subTitle_2', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page7_subTitle_2_p', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page7_subTitle_2_p2', true)}
        </Text>
        <Text style={[styles.title, styles.titleFont12]}>
          {useTranslation('pdf_page7_subTitle_3', true)}
        </Text>
        <Text style={styles.subTitle}>
          {useTranslation('pdf_page7_subTitle_3_p', true)}
        </Text>
        <Text style={styles.bold}>
          {useTranslation('pdf_page7_subTitle_4', true)}
        </Text>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
